import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  PROBLEM,
  INTUITION,
  SHAPE,
  AUTH,
  TOOLS,
  GOTCHAS,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1500,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const on = interpolate(frame, [40, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="eakc · mcp server" color={COLORS.cyan} />
          <Big>
            My AI IDE answers
            <br />
            from{" "}
            <span style={{ color: COLORS.green, opacity: 0.3 + on * 0.7 }}>
              MY knowledge base
            </span>
          </Big>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 26,
              color: COLORS.dim,
              marginTop: 28,
            }}
          >
            one endpoint · any MCP client · no browser tab
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the tab-switch tax" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="amber" title="before · web app" width={480}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 21, lineHeight: 1.85, color: COLORS.text }}>
              <div>1 · leave your editor</div>
              <div>2 · open a browser tab</div>
              <div>3 · type the question</div>
              <div>4 · copy the answer back</div>
              <div style={{ color: COLORS.amber }}>↳ context-switch, every time</div>
            </div>
          </Panel>
          <Panel accent="green" title="after · in the IDE" width={480}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 21, lineHeight: 1.85, color: COLORS.text }}>
              <div>ask, right where you code</div>
              <div style={{ color: COLORS.dim }}>answer + file paths + snippets</div>
              <div style={{ marginTop: 18, color: COLORS.green, fontWeight: 600 }}>
                MCP server
              </div>
              <div style={{ color: COLORS.dim }}>one weekend · 3 tools to start</div>
            </div>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="amber" />
  </AbsoluteFill>
);

/* 03 — INTUITION */
export const Scene03Intuition: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.intuition.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="intuition · usb for ai" color={COLORS.violet} />
        <Big size={70}>One server.</Big>
        <div style={{ display: "flex", gap: 22, marginTop: 32, flexWrap: "wrap", justifyContent: "center" }}>
          {["Claude Desktop", "Cursor", "Windsurf"].map((c) => (
            <div
              key={c}
              style={{
                fontFamily: FONTS.mono,
                fontSize: 26,
                color: COLORS.text,
                background: COLORS.panel,
                border: `1px solid ${COLORS.violet}66`,
                borderRadius: 10,
                padding: "14px 26px",
              }}
            >
              {c}
            </div>
          ))}
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.dim, marginTop: 30 }}>
          one standard protocol — any client speaks it
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INTUITION} accent="violet" />
  </AbsoluteFill>
);

/* 04 — SHAPE (one route, JSON-RPC) */
const ROUTE = `// src/app/api/mcp/route.ts
const SERVER_NAME = 'eakc-mcp'
const PROTOCOL_VERSION = '2024-11-05'

export async function POST(req: NextRequest) {
  const apiKey = req.headers.get('x-api-key')
  const auth   = await validateMcpApiKey(apiKey)   // → tenantId

  switch (method) {
    case 'initialize':   return serverInfo(...)
    case 'tools/list':   return { tools: ALL_MCP_TOOLS }
    case 'tools/call':   return dispatch(toolName, auth.tenantId)
  }
}`;
export const Scene04Shape: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.shape.duration} />
    <AbsoluteFill style={{ ...center, paddingBottom: 30 }}>
      <Kicker text="one route · POST /api/mcp" color={COLORS.cyan} />
      <CodePanel
        code={ROUTE}
        language="tsx"
        filename="src/app/api/mcp/route.ts"
        highlightLines={[6, 10, 11, 12]}
        revealFrames={200}
        fontSize={23}
        width={1180}
      />
    </AbsoluteFill>
    <Caption lines={SHAPE} accent="cyan" />
  </AbsoluteFill>
);

/* 05 — AUTH pipeline */
const AUTH_PIPE: PipeNode[] = [
  { label: "client", value: "X-API-Key", sub: "header", accent: "cyan" },
  { label: "hash", value: "SHA-256", sub: "raw key dropped", accent: "green" },
  { label: "lookup", value: "mcp_api_keys", sub: "mig 020", accent: "violet" },
  { label: "scope", value: "tenant_id", sub: "every tool", accent: "amber" },
];
export const Scene05Auth: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.auth.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="auth · api key, not a session" color={COLORS.green} />
      <div style={{ width: 1640 }}>
        <PipelineGraph nodes={AUTH_PIPE} startFrame={8} stepFrames={24} nodeWidth={230} />
      </div>
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 22,
          color: COLORS.dim,
          marginTop: 46,
        }}
      >
        60 req/min per key · raw key never persisted, only its hash
      </div>
    </AbsoluteFill>
    <Caption lines={AUTH} accent="green" />
  </AbsoluteFill>
);

/* 06 — TOOLS (3 → 15) */
const TOOL_ROWS: { name: string; note: string; accent: "cyan" | "green" | "violet" | "amber" }[] = [
  { name: "query_eakc", note: "RAG answer + chunk IDs", accent: "cyan" },
  { name: "list_sources", note: "source catalog (no secrets)", accent: "cyan" },
  { name: "get_document", note: "chunk by id · dual isolation", accent: "cyan" },
  { name: "graph_query", note: "Graphify traversal", accent: "green" },
  { name: "generate", note: "email / brief / battle card", accent: "green" },
  { name: "invoke_agent", note: "delegate to an EAKC agent", accent: "violet" },
  { name: "ingest_document", note: "write · can_write + review", accent: "amber" },
];
export const Scene06Tools: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.tools.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="the tools it exposes" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 40, alignItems: "center" }}>
          <Panel accent="cyan" title="tools/list" width={760} padding={20}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {TOOL_ROWS.map((t, i) => {
                const appear = interpolate(frame, [20 + i * 12, 40 + i * 12], [0, 1], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                });
                return (
                  <div
                    key={t.name}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      opacity: appear,
                      transform: `translateX(${(1 - appear) * 14}px)`,
                      borderLeft: `3px solid ${COLORS[t.accent]}`,
                      paddingLeft: 14,
                    }}
                  >
                    <span style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.text }}>
                      {t.name}
                    </span>
                    <span style={{ fontFamily: FONTS.mono, fontSize: 17, color: COLORS.dim }}>
                      {t.note}
                    </span>
                  </div>
                );
              })}
            </div>
          </Panel>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim, letterSpacing: "0.1em" }}>
              shipped 3 →
            </div>
            <MetricCounter value={15} from={3} startFrame={70} durationFrames={50} accent="lime" fontSize={150} />
            <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim }}>tools today</div>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={TOOLS} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 07 — GOTCHAS */
export const Scene07Gotchas: React.FC = () => {
  const frame = useCurrentFrame();
  const g = (start: number) =>
    interpolate(frame, [start, start + 24], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.gotchas.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="protocol gotchas · a weekend of debugging" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 26, alignItems: "stretch" }}>
          <div style={{ opacity: g(20) }}>
            <Panel accent="red" title="01 · the handshake" width={500}>
              <div style={{ fontFamily: FONTS.body, fontSize: 22, lineHeight: 1.5, color: COLORS.text }}>
                <code style={{ fontFamily: FONTS.mono, color: COLORS.cyan }}>notifications/initialized</code>{" "}
                must return <b style={{ color: COLORS.green }}>200</b>, not{" "}
                <b style={{ color: COLORS.red }}>204</b>. Cursor treats 204 as a transport failure.
              </div>
            </Panel>
          </div>
          <div style={{ opacity: g(110) }}>
            <Panel accent="amber" title="02 · tool errors" width={500}>
              <div style={{ fontFamily: FONTS.body, fontSize: 22, lineHeight: 1.5, color: COLORS.text }}>
                Failures use{" "}
                <code style={{ fontFamily: FONTS.mono, color: COLORS.amber }}>isError: true</code>{" "}
                in the result — not JSON-RPC error codes, which clients drop as transport breaks.
              </div>
            </Panel>
          </div>
          <div style={{ opacity: g(200) }}>
            <Panel accent="green" title="03 · backoff" width={500}>
              <div style={{ fontFamily: FONTS.body, fontSize: 22, lineHeight: 1.5, color: COLORS.text }}>
                Send{" "}
                <code style={{ fontFamily: FONTS.mono, color: COLORS.green }}>X-RateLimit-*</code>{" "}
                headers on <b style={{ color: COLORS.text }}>every</b> response, so clients know when to retry.
              </div>
            </Panel>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={GOTCHAS} accent="amber" />
    </AbsoluteFill>
  );
};

/* 08 — PAYOFF */
export const Scene08Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [110, 140], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="what it unlocks" color={COLORS.lime} />
        <Big size={66}>
          Ask in your editor.
          <br />
          <span style={{ color: COLORS.lime }}>Answered from the codebase.</span>
        </Big>
        <div style={{ display: "flex", gap: 30, marginTop: 40 }}>
          <Callout label="grounded" accent="green" width={360}>
            Real file paths + snippets, <b style={{ color: COLORS.text }}>tenant-scoped</b>, with citations.
          </Callout>
          <div style={{ opacity: c1 }}>
            <Callout label="future-proof" accent="lime" width={360}>
              Any new MCP IDE connects with <b style={{ color: COLORS.text }}>zero server changes</b>.
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 09 — OUTRO */
export const Scene09Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          One endpoint. API-key auth.
          <br />
          <span style={{ color: COLORS.cyan }}>The KB lives where you code.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · MCP server
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
